'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import RangeSlider from '@/components/ui/rangeSlider';
import { trackStatistic } from '@/lib/utils/statisticTracker';
import SidebarSection from './SidebarSection';
import { useSidebarFilter } from './useSidebarFilter';

type SalaryRange = [number, number];

const FULL_TIME_SALARY_MAX = 20000000;
const PART_TIME_SALARY_MAX = 100000;

const getDefaultSalaryRange = (partTime: boolean): SalaryRange => [
  0,
  partTime ? PART_TIME_SALARY_MAX : FULL_TIME_SALARY_MAX,
];

const isDefaultSalaryRange = (range: SalaryRange, partTime: boolean) => {
  const [min, max] = getDefaultSalaryRange(partTime);
  return range[0] === min && range[1] === max;
};

const isSameSalaryRange = (a: SalaryRange, b: SalaryRange) =>
  a[0] === b[0] && a[1] === b[1];

const SalaryComponent = () => {
  const { searchParams, router } = useSidebarFilter();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [salaryRange, setSalaryRange] = useState<SalaryRange>(
    getDefaultSalaryRange(false)
  );
  const [isPartTime, setIsPartTime] = useState(false);

  const isPartTimeParam = (searchParams.get('workTypes') || '')
    .split(',')
    .includes('2');

  useEffect(() => {
    const salaryMin = searchParams.get('salaryMin');
    const salaryMax = searchParams.get('salaryMax');
    const newParams = new URLSearchParams(searchParams.toString());
    const defaultRange = getDefaultSalaryRange(isPartTimeParam);

    const syncSalaryRange = (next: SalaryRange) => {
      setSalaryRange((prev) => (isSameSalaryRange(prev, next) ? prev : next));
    };

    if (isPartTimeParam) {
      setIsPartTime(true);
      if (
        (salaryMax && parseInt(salaryMax) > PART_TIME_SALARY_MAX) ||
        (salaryMin && parseInt(salaryMin) > PART_TIME_SALARY_MAX)
      ) {
        newParams.delete('salaryMin');
        newParams.delete('salaryMax');
        syncSalaryRange(defaultRange);
        router.replace(`/job/list?${newParams.toString()}`);
      } else if (salaryMin || salaryMax) {
        syncSalaryRange([
          salaryMin ? parseInt(salaryMin) : 0,
          salaryMax ? parseInt(salaryMax) : PART_TIME_SALARY_MAX,
        ]);
      } else {
        syncSalaryRange(defaultRange);
      }
    } else if (isPartTime) {
      setIsPartTime(false);
      newParams.delete('salaryMin');
      newParams.delete('salaryMax');
      syncSalaryRange(getDefaultSalaryRange(false));
      router.replace(`/job/list?${newParams.toString()}`);
    } else if (salaryMin || salaryMax) {
      syncSalaryRange([
        salaryMin ? parseInt(salaryMin) : 0,
        salaryMax ? parseInt(salaryMax) : FULL_TIME_SALARY_MAX,
      ]);
    } else {
      syncSalaryRange(getDefaultSalaryRange(false));
    }
  }, [searchParams, isPartTime, isPartTimeParam, router]);

  const debouncedUpdateSalary = useCallback(
    (nextSalaryRange: SalaryRange, partTime: boolean) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const params = new URLSearchParams(searchParams.toString());

        if (isDefaultSalaryRange(nextSalaryRange, partTime)) {
          params.delete('salaryMin');
          params.delete('salaryMax');
        } else {
          params.set('salaryMin', nextSalaryRange[0].toString());
          params.set('salaryMax', nextSalaryRange[1].toString());
        }

        params.delete('page');
        router.push(`/job/list?${params.toString()}`, { scroll: false });
      }, 300);
    },
    [router, searchParams]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSliderChange = (value: SalaryRange) => {
    setSalaryRange(value);
  };

  const handleChangeComplete = (value: SalaryRange) => {
    setSalaryRange(value);
    trackStatistic('sidebar_filter');
    debouncedUpdateSalary(value, isPartTime);
  };

  const formatSalary = (value: number): string => {
    return new Intl.NumberFormat('mn-MN').format(value) + '₮';
  };

  return (
    <SidebarSection title="Цалин">
      <div className="w-full px-1 mx-auto">
        <RangeSlider
          min={0}
          max={isPartTime ? PART_TIME_SALARY_MAX : FULL_TIME_SALARY_MAX}
          step={isPartTime ? 10000 : 100000}
          onChange={(value: number | number[]) =>
            handleSliderChange(value as [number, number])
          }
          onChangeComplete={(value: number | number[]) =>
            handleChangeComplete(value as [number, number])
          }
          salaryRange={salaryRange}
        />
        <div className="flex justify-between text-sm mt-2">
          <span>{formatSalary(salaryRange[0])}</span>
          <span>
            {salaryRange[1] ===
            (isPartTime ? PART_TIME_SALARY_MAX : FULL_TIME_SALARY_MAX)
              ? formatSalary(salaryRange[1]) + '-с дээш'
              : formatSalary(salaryRange[1])}
          </span>
        </div>
      </div>
    </SidebarSection>
  );
};

export default SalaryComponent;
